// helpers/hooks/useConversationActions.tsx
import React, { useCallback, useEffect, useMemo, useRef } from 'react'

import type { QueryClient } from 'react-query'
import { useMutation } from 'react-query'

import * as spriteIcons from 'assets/svg/sprite'
import { addToast } from 'components/Toast/Toast'
import BlockUser from 'components/UI/Modal/BlockUser/BlockUser'
import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
// import Report from 'components/UI/Modal/Report/Report'
import SharedMediaModalChat from 'components/UI/Modal/SharedMediaModal/SharedMediaModalChat'
import TipModal from 'components/UI/Modal/Tip/TipModal'
import UserDetails from 'components/UI/Modal/UserDetails/UserDetails'
import { replaceOptimisticWithServer } from 'helpers/chat/conversationCache'
import { useRefetchUserProfile } from 'helpers/hooks/useRefetchUserProfile/useRefetchUserProfile'
import {
  createConversation,
  deleteConversation,
  deleteConversationMessage,
  muteConversation,
  purchaseMessage,
  reactToMessage,
  sendConversationMessage,
} from 'services/endpoints/message'
import { reportMessage } from 'services/endpoints/report'

type HeaderActionElement = {
  icon: React.ReactNode
  text: string
  clickFn: () => void
}

type UseConversationActionsArgs = {
  t: any
  id: any
  userData: any
  otherUserData: any
  activeSubscription?: boolean
  conversationId: string | null
  setConversationId: (id: string | null) => void
  queryClient: QueryClient
  modalData: any
  navigate: (to: string, opts?: any) => void

  // shared media
  submitFn: (val: any) => void

  // purchase flow
  setPurchaseMsgId: (id: string) => void
  setConfirmBuyModalOpen: (open: boolean) => void
  setSelectedMessageBuyPrice: (price: number) => void

  // helpers
  scrollToMessage: (id: string) => void
  scrollToBottom: () => void
}

const DEFAULT_REACTIONS = {
  cry: { count: 0, users: [] },
  kiss: { count: 0, users: [] },
  like: { count: 0, users: [] },
  love: { count: 0, users: [] },
  sad: { count: 0, users: [] },
  smile: { count: 0, users: [] },
}

type ConversationRow = any

function conversationKey(conversationId: string | null, searchTerm = '') {
  return ['conversation', conversationId, searchTerm.trim()] as const
}

// -----------------------------
// Cache helpers (minimal)
// -----------------------------

function safeGetPages(old: any): { pages: any[]; pageParams: any[] } | null {
  if (!old || !Array.isArray(old.pages)) return null
  return { pages: old.pages, pageParams: old.pageParams ?? [] }
}

function appendToFirstPage(old: any, row: ConversationRow): any {
  const base = safeGetPages(old)
  if (!base) return old

  const pages = [...base.pages]
  const first = pages[0] ?? {}
  const data = Array.isArray(first.data) ? [...first.data] : []

  pages[0] = { ...first, data: [...data, row] }
  return { ...old, pages }
}

function removeRowByTempId(old: any, tempId: string): any {
  const base = safeGetPages(old)
  if (!base) return old

  const pages = base.pages.map((p: any) => {
    if (!Array.isArray(p?.data)) return p
    const data = p.data.filter((m: any) => String(m?.id) !== String(tempId))
    return { ...p, data }
  })

  return { ...old, pages }
}

function removeRowByServerMessageId(old: any, messageId: string): any {
  const base = safeGetPages(old)
  if (!base) return old

  const pages = base.pages.map((p: any) => {
    if (!Array.isArray(p?.data)) return p
    const data = p.data.filter(
      (m: any) => String(m?.message?.id) !== String(messageId)
    )
    return { ...p, data }
  })

  return { ...old, pages }
}

function updateRowByServerMessageId(
  old: any,
  messageId: string | number,
  updater: (row: any) => any
): any {
  const base = safeGetPages(old)
  if (!base) return old

  const pages = base.pages.map((p: any) => {
    if (!Array.isArray(p?.data)) return p

    const data = p.data.map((m: any) => {
      if (String(m?.message?.id) !== String(messageId)) return m
      return updater(m)
    })

    return { ...p, data }
  })

  return { ...old, pages }
}

function makeOptimisticRow(params: {
  tempId: string
  myId: string
  message: any
}): ConversationRow {
  const { tempId, myId, message } = params

  return {
    id: tempId, // temp wrapper id
    sending: true,
    seen: [],
    purchased: [],
    reactions: DEFAULT_REACTIONS,
    body: '',
    message: {
      ...message,
      is_purchased: true,
      created_at:
        message?.created_at ?? new Date().toISOString(),
      sender_id: myId,
      is_sender: true,
    },
    sender_id: myId,
  }
}

// -----------------------------
// Hook
// -----------------------------

export function useConversationActions({
  t,
  id,
  userData,
  otherUserData,
  activeSubscription,
  conversationId,
  setConversationId,
  queryClient,
  modalData,
  navigate,

  submitFn,

  setPurchaseMsgId,
  setConfirmBuyModalOpen,
  setSelectedMessageBuyPrice,

  scrollToMessage,
}: UseConversationActionsArgs) {
  const { refetchUserProfile } = useRefetchUserProfile()


const showUserDetails =
  userData?.role === 'model' &&
  otherUserData?.show_fan_options !== true

  // DONT TRIGGER CONVERSATIONS QUERY WHEN CONVERSATION OPEN
  const conversationsRefreshBlockedRef = useRef(true)

  useEffect(() => {
    conversationsRefreshBlockedRef.current = true

    return () => {
      conversationsRefreshBlockedRef.current = false

      if (conversationId) {
        queryClient.invalidateQueries(
          conversationKey(conversationId)
        )
      }

      queryClient.invalidateQueries(['conversations'])
    }
  }, [queryClient])

  // -----------------------------
  // Mutations
  // -----------------------------

  /**
   * IMPORTANT:
   * sendConversationMessage() is ACK-only (e.g. "Message sent successfully")
   * It does NOT return the created row.
   * Real rows enter cache via:
   *  - initial fetch (GET), and/or
   *  - Pusher inserts.
   *
   * So onSuccess we ONLY mark optimistic row as sending:false.
   */

  const addMessageMutation = useMutation(
    async (vars: {
      message: any
      tempId: string
      isScheduled?: boolean
    }) => {
      const cid = conversationId ?? null

      if (cid) {
        const row = await sendConversationMessage(
          vars.message,
          cid
        )

        return {
          cid,
          tempId: vars.tempId,
          row,
          isScheduled: !!vars.isScheduled,
        }
      }

      const createdConversation =
        await createConversation(otherUserData.id)

      const newConversationId =
        createdConversation.data.id

      setConversationId(newConversationId)

      const row = await sendConversationMessage(
        vars.message,
        newConversationId
      )

      return {
        cid: newConversationId,
        tempId: vars.tempId,
        row,
        isScheduled: !!vars.isScheduled,
      }
    },
    {
      onSuccess: ({
        cid,
        tempId,
        row,
        isScheduled,
      }) => {
        if (!cid) return

        // Scheduled messages should NEVER touch chat cache
        if (isScheduled) {
          addToast('success', t('messageScheduled'))
          return
        }

        // ACK fallback
        if (!row) {
          queryClient.setQueryData(
            conversationKey(cid),
            (old: any) => {
              const base = safeGetPages(old)
              if (!base) return old

              const pages = base.pages.map((p: any) => {
                if (!Array.isArray(p?.data)) return p

                return {
                  ...p,
                  data: p.data.map((r: any) =>
                    String(r?.id) === String(tempId)
                      ? { ...r, sending: false }
                      : r
                  ),
                }
              })

              return { ...old, pages }
            }
          )

          return
        }

        const stamped = {
          ...row,
          __client_debug: {
            replacedOptimistic: true,
            replacedAt: new Date().toISOString(),
            tempId,
          },
        }

        replaceOptimisticWithServer(
          queryClient,
          cid,
          tempId,
          stamped
        )
      },

      onError: (err: any, vars) => {
        if (conversationId) {
          queryClient.setQueryData(
            conversationKey(conversationId),
            (old: any) =>
              removeRowByTempId(old, vars.tempId)
          )
        }

        if (err?.response?.data?.error?.message) {
          addToast(
            'error',
            err.response.data.error.message
          )
        } else {
          addToast(
            'error',
            t('error:errorSomethingWentWrong')
          )
        }
      },
    }
  )

  const addReactionMutation = useMutation(
    async (message: {
      type: string
      id: string
    }) => {
      return await reactToMessage(
        message.type,
        message.id
      )
    }
  )

  const deleteConversationMutation = useMutation(
    async () =>
      await deleteConversation(conversationId),
    {
      onSuccess: () => {
        addToast('success', t('successfullyDeleted'))

        if (
          !conversationsRefreshBlockedRef.current &&
          conversationId
        ) {
          queryClient.invalidateQueries(
            conversationKey(conversationId)
          )
        }

        navigate('/chat', { replace: true })
      },
    }
  )

  const muteConversationMutation = useMutation(
    async () =>
      await muteConversation({
        new_message:
          otherUserData?.is_muted ? 0 : 1,
        other_user_id: otherUserData.id,
      }),
    {
      onSuccess: () => {
        addToast(
          'success',
          otherUserData?.is_muted
            ? t('unmuted')
            : t('muted')
        )

        queryClient.invalidateQueries([
          'profile',
          id,
        ])
      },
    }
  )

  const purchaseMutation = useMutation(
    async ({
      id,
      paymentMethod,
      cardId,
      taxRate,
      taxRateFee,
    }: any) =>
      await purchaseMessage(
        id,
        paymentMethod,
        cardId,
        taxRate,
        taxRateFee
      ),
    {
      onSuccess: resp => {
        setConfirmBuyModalOpen(false)

        // purchase endpoint SHOULD return a row (your existing code assumes it does)
        const row = resp?.data
        const msgId = row?.message?.id

        if (conversationId && msgId) {
          queryClient.setQueryData(
            conversationKey(conversationId),
            (old: any) =>
              updateRowByServerMessageId(
                old,
                msgId,
                () => row
              )
          )
        }

        // refetchUserProfile()
        modalData.clearModal()
      },
    }
  )

  const reportMutation = useMutation(
    async (params: {
      id: string
      report_type_id: number
      text: string
    }) =>
      await reportMessage({
        id: params.id,
        reportData: {
          report_type_id:
            params.report_type_id,
          text: params.text,
        },
      }),
    {
      onSuccess: () => {
        modalData.clearModal()

        addToast(
          'success',
          t('messageReported')
        )
      },
    }
  )

  const deleteMessageMutation = useMutation(
    deleteConversationMessage,
    {
      onSuccess: (_resp, messageId: string) => {
        if (conversationId && messageId) {
          queryClient.setQueryData(
            conversationKey(conversationId),
            (old: any) =>
              removeRowByServerMessageId(
                old,
                messageId
              )
          )
        }

        if (conversationId) {
          queryClient.invalidateQueries(
            conversationKey(conversationId)
          )
        }
      },
    }
  )

  // -----------------------------
  // Handlers
  // -----------------------------

  const addTip = useCallback(() => {
      modalData.addModal(
          t('tipAmount'),
          <TipModal
              modelData={{
                  modelId: otherUserData.id,
                  avatarSrc: otherUserData.avatar.url,
              }}
          />,
          false,
          false,
          '',
          '',
          true // keyboardAdjust
      )
  }, [modalData, otherUserData, t])

  const addReaction = useCallback(
    (type: string, reactionId: string) => {
      if (
        userData?.status === 'restricted'
      ) {
        addToast(
          'error',
          t('messageReactRestricted')
        )
        return
      }

      if (!conversationId) return

      // Optimistic patch cache
      queryClient.setQueryData(
        conversationKey(conversationId),
        (old: any) =>
          updateRowByServerMessageId(
            old,
            reactionId,
            (row: any) => {
              const myId = userData?.id
              if (!myId) return row

              const reactions =
                row?.reactions ??
                DEFAULT_REACTIONS

              const next = structuredClone
                ? structuredClone(reactions)
                : JSON.parse(
                    JSON.stringify(reactions)
                  )

              const keys = Object.keys(next)

              const already = (
                next?.[type]?.users ?? []
              ).some(
                (u: any) =>
                  String(u?.id) ===
                  String(myId)
              )

              if (!already) {
                // remove prior reactions by me
                for (const k of keys) {
                  const users = Array.isArray(
                    next[k]?.users
                  )
                    ? next[k].users
                    : []

                  const before = users.length

                  next[k].users = users.filter(
                    (u: any) =>
                      String(u?.id) !==
                      String(myId)
                  )

                  const after =
                    next[k].users.length

                  if (
                    before !== after &&
                    typeof next[k].count ===
                      'number' &&
                    next[k].count > 0
                  ) {
                    next[k].count -= 1
                  }
                }

                next[type].users = [
                  ...(next[type].users ?? []),
                  userData,
                ]

                next[type].count =
                  (next[type].count ?? 0) + 1
              } else {
                next[type].users = (
                  next[type].users ?? []
                ).filter(
                  (u: any) =>
                    String(u?.id) !==
                    String(myId)
                )

                next[type].count = Math.max(
                  0,
                  (next[type].count ?? 0) - 1
                )
              }

              return {
                ...row,
                reactions: next,
              }
            }
          )
      )

      addReactionMutation.mutate(
        {
          type,
          id: reactionId,
        },
        {
          onError: () => {
            queryClient.invalidateQueries(
              conversationKey(conversationId)
            )
          },
        }
      )
    },
    [
      addReactionMutation,
      conversationId,
      queryClient,
      t,
      userData,
    ]
  )

  const deleteMessage = useCallback(
    (messageId: string) => {
      deleteMessageMutation.mutate(
        messageId
      )
    },
    [deleteMessageMutation]
  )

  const reportMessageHandler = useCallback(
    (
      messageId: string,
      text: string,
      reportType: number
    ) => {
      reportMutation.mutate({
        id: messageId,
        text,
        report_type_id: reportType,
      })
    },
    [reportMutation]
  )

  const openPurchase = useCallback(
    (msgId: string, price: number) => {
      setPurchaseMsgId(msgId)
      setConfirmBuyModalOpen(true)
      setSelectedMessageBuyPrice(price)
    },
    [
      setConfirmBuyModalOpen,
      setPurchaseMsgId,
      setSelectedMessageBuyPrice,
    ]
  )

  const sendGif = useCallback(
    (val: any) => {
      if (
        userData?.status === 'restricted'
      ) {
        addToast(
          'error',
          t('restrictedMessageSend')
        )
        return
      }

      const myId = userData?.id ?? ''
      const tempId = `tmp-${Date.now()}`

      const messageObj = {
        gif: val.id,
        photos: [],
        videos: [],
        sounds: [],
        message: '',
        price: 0,
        created_at:
          new Date().toISOString(),
      }

      const optimisticRow =
        makeOptimisticRow({
          tempId,
          myId,
          message: messageObj,
        })

      if (conversationId) {
        queryClient.setQueryData(
          conversationKey(conversationId),
          (old: any) =>
            appendToFirstPage(
              old,
              optimisticRow
            )
        )
      }

      addMessageMutation.mutate({
        message: { ...messageObj },
        tempId,
      })
    },
    [
      addMessageMutation,
      conversationId,
      queryClient,
      t,
      userData,
    ]
  )

  const sendMessage = useCallback(
    (val: any) => {
      const myId = userData?.id ?? ''
      const tempId = `tmp-${Date.now()}`

      const optimisticMessage = {
        ...val,
        created_at:
          new Date().toISOString(),
        sender_id: myId,
        videos: (val.videos ?? []).map(
          (v: any) => ({
            ...v,
            status: 'processing',
            user_id: myId,
          })
        ),
        is_sender: true,
      }

      const optimisticRow =
        makeOptimisticRow({
          tempId,
          myId,
          message: optimisticMessage,
        })

      const isScheduled =
        !!val.schedule_date

      if (
        !isScheduled &&
        conversationId
      ) {
        queryClient.setQueryData(
          conversationKey(conversationId),
          (old: any) =>
            appendToFirstPage(
              old,
              optimisticRow
            )
        )

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            scrollToMessage(
              optimisticRow.message.id
            )
          })
        })
      }

const payloadForBackend = {
  ...val,

  id: tempId,

  photos: (val.photos ?? []).map(
    (photo: any) => {
      const {
        url,
        thumb_url,
        thumb_path,
        ...rest
      } = photo

      return rest
    }
  ),

  videos: (val.videos ?? []).map(
    (v: any) => {
      const {
        status,
        user_id,
        ...rest
      } = v

      return rest
    }
  ),
}
      addMessageMutation.mutate({
        message: payloadForBackend,
        tempId,
        isScheduled,
      })
    },
    [
      addMessageMutation,
      conversationId,
      queryClient,
      t,
      userData,
    ]
  )

  const headerActionElements: HeaderActionElement[] =
    useMemo(() => {
      const elems: HeaderActionElement[] = []

      if (showUserDetails) {
        elems.push({
          icon: <spriteIcons.IconManOutline />,
          text: 'User Details',
          clickFn: () => {
            if (otherUserData?.deleted) {
              addToast('error', t('userDeleted'))
              return
            }

            modalData.addModal(
              '',
              <UserDetails user={otherUserData} />,
              true
            )
          },
        })
      }
      elems.push({
        icon: (
          <spriteIcons.IconSharedFiles />
        ),
        text: 'Shared Files',
        clickFn: () => {
          modalData.addModal(
            '',
            <SharedMediaModalChat
              submitFn={submitFn}
              user={otherUserData}
              setPurchaseMsgId={
                setPurchaseMsgId
              }
              setConfirmBuyModalOpen={
                setConfirmBuyModalOpen
              }
              setSelectedMessageBuyPrice={
                setSelectedMessageBuyPrice
              }
              scrollToMessage={
                scrollToMessage
              }
              sharedWith={
                otherUserData.username
              }
            />,
            true,
            true
          )
        },
      })
/*       elems.push({
        icon: (
          <spriteIcons.IconMuteOutline />
        ),
        text: otherUserData?.is_muted
          ? 'Unmute Conversation'
          : 'Mute Conversation',
        clickFn: () => {
          if (otherUserData?.deleted) {
            addToast(
              'error',
              t('userDeleted')
            )
            return
          }

          muteConversationMutation.mutate()
        },
      }) */

        elems.push({
          icon: (
            <spriteIcons.IconManOutline />
          ),
          text: 'Block User',
          clickFn: () => {
            if (otherUserData?.deleted) {
              addToast(
                'error',
                t('userDeleted')
              )
              return
            }

            modalData.addModal(
              t('block'),
              <BlockUser
                user={otherUserData}
              />
            )
          },
        })
      elems.push({
        icon: (
          <spriteIcons.IconTrashcanLarge
            width="17"
            height="20"
            color="#778797"
          />
        ),
        text: 'Delete Conversation',
        clickFn: () => {
          modalData.addModal(
            t('deleteConversation'),
            <ConfirmModal
              body={t(
                'onceYouDeleteYourCopyOfTheConversation'
              )}
              confirmFn={() =>
                deleteConversationMutation.mutate()
              }
            />
          )
        },
      })

      return elems
    }, [
      deleteConversationMutation,
      modalData,
      muteConversationMutation,
      otherUserData,
      scrollToMessage,
      setConfirmBuyModalOpen,
      setPurchaseMsgId,
      setSelectedMessageBuyPrice,
      submitFn,
      t,
      userData?.role,
    ])

  return {
    headerActionElements,

    addTip,
    addReaction,
    deleteMessage,
    reportMessage:
      reportMessageHandler,
    openPurchase,
    sendGif,
    sendMessage,

    purchaseMutation,
  }
}